import {createTypesenseClient} from "@buildwithflux/search";
import {useEffect, useState} from "react";
import type {Client} from "typesense";

import {useTypesenseIdToken} from "../context/TypesenseIdTokenContext";
import {useTypesenseSearchTelemetry} from "../context/TypesenseSearchTelemetryContext";
import {fetchTypesenseSearchCredentials} from "../typesenseAuth";
import {getTypesenseSearchCluster} from "../typesenseCredentials";
import {instrumentTypesenseSearches} from "../typesenseSearchTelemetry";

import type {ClientInitialization} from "./clientInitialization";

const CREDENTIAL_REFRESH_MS = 5 * 60 * 1_000;

/** Pass `enabled: false` on surfaces that never search these collections. */
export function useTypesenseClientInitialization(
    enabled = true,
): ClientInitialization<{readonly projects: Client; readonly parts: Client}> {
    const getIdToken = useTypesenseIdToken();
    const recordTypesenseSearch = useTypesenseSearchTelemetry();
    const [result, setResult] = useState<ClientInitialization<{readonly projects: Client; readonly parts: Client}>>({
        client: null,
        status: enabled ? "loading" : "disabled",
    });

    useEffect(() => {
        if (!enabled) {
            setResult({client: null, status: "disabled"});
            return;
        }
        let cancelled = false;
        let inFlight = false;
        let lastRequestedAt = 0;
        let lastCredentialFingerprint: string | undefined;
        setResult({client: null, status: "loading"});

        const refresh = () => {
            if (inFlight || document.visibilityState !== "visible") return;
            inFlight = true;
            lastRequestedAt = Date.now();
            fetchTypesenseSearchCredentials(getIdToken)
                .then((credentials) => {
                    if (cancelled) return;
                    const fingerprint = [
                        credentials.projects.host,
                        credentials.projects.apiKey,
                        credentials.parts.host,
                        credentials.parts.apiKey,
                    ].join("|");
                    if (fingerprint === lastCredentialFingerprint) return;
                    lastCredentialFingerprint = fingerprint;
                    setResult({
                        client: {
                            projects: instrumentTypesenseSearches(
                                createTypesenseClient({
                                    host: credentials.projects.host,
                                    port: 443,
                                    protocol: "https",
                                    apiKey: credentials.projects.apiKey,
                                    cacheSearchResultsForSeconds: 10,
                                }),
                                getTypesenseSearchCluster(credentials.projects),
                                "projects",
                                recordTypesenseSearch,
                            ),
                            parts: instrumentTypesenseSearches(
                                createTypesenseClient({
                                    host: credentials.parts.host,
                                    port: 443,
                                    protocol: "https",
                                    apiKey: credentials.parts.apiKey,
                                    cacheSearchResultsForSeconds: 10,
                                }),
                                getTypesenseSearchCluster(credentials.parts),
                                "parts",
                                recordTypesenseSearch,
                            ),
                        },
                        status: "ready",
                    });
                })
                .catch((err) => {
                    if (cancelled) return;
                    // eslint-disable-next-line no-console
                    console.error("Failed to initialize Typesense client:", err);
                    setResult((previous) => (previous.client ? previous : {client: null, status: "error"}));
                })
                .finally(() => {
                    inFlight = false;
                });
        };
        refresh();
        const timer = setInterval(refresh, CREDENTIAL_REFRESH_MS);
        const refreshOnVisible = () => {
            if (document.visibilityState === "visible" && Date.now() - lastRequestedAt >= CREDENTIAL_REFRESH_MS) {
                refresh();
            }
        };
        document.addEventListener("visibilitychange", refreshOnVisible);

        return () => {
            cancelled = true;
            clearInterval(timer);
            document.removeEventListener("visibilitychange", refreshOnVisible);
        };
    }, [enabled, getIdToken, recordTypesenseSearch]);

    return result;
}
